import React, { useEffect } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { UiFieldset, InnerLoader } from 'src/ui';
import { Columns, Column } from 'src/ui/mui-ui-kit';
import { fetchMerchantFees } from 'src/payment/redux';

function FeeBreakdownComponent({ account, user, fees, loading, error, fetchMerchantFees }) {
  useEffect(() => {
    if (user.financialInstitutionId && account.id) {
      fetchMerchantFees({
        fi: user.financialInstitutionId,
        account: account.id,
      });
    }
  }, [user.financialInstitutionId, account.id, fetchMerchantFees]);

  if (loading) {
    return (
      <UiFieldset title="Fee Breakdown" isFirst>
        <InnerLoader visible={true} />
      </UiFieldset>
    );
  }

  if (error) {
    return (
      <UiFieldset title="Fee Breakdown" isFirst>
        <div className="notification is-warning">
          <p>{error}</p>
        </div>
      </UiFieldset>
    );
  }

  if (!fees) {
    return null;
  }

  const getFeePayerLabel = (feePayer) => {
    const labels = {
      'merchant': 'Merchant Pays All Fees',
      'customer': 'Customer Pays All Fees',
      'shared': 'Shared (Each Pays Their Configured Fees)',
    };
    return labels[feePayer] || feePayer;
  };

  return (
    <UiFieldset title="Fee Breakdown" isFirst>
      <div className="content">
        <p className="has-text-grey">
          Read-only view of current fee configuration for PYUSD payment processing.
        </p>
      </div>
      
      <Columns isMultiline>
        {/* Fee Payer */}
        <Column isSize="1/1">
          <div className="field">
            <label className="label">Fee Payer</label>
            <div className="control">
              <input 
                className="input" 
                type="text" 
                value={getFeePayerLabel(fees.fee_payer)} 
                readOnly 
              />
            </div>
            <p className="help">Who is responsible for paying transaction fees</p>
          </div>
        </Column>

        {/* Merchant Fees */}
        <Column isSize="1/2">
          <div className="field">
            <label className="label">Merchant Fee (Percentage)</label>
            <div className="control">
              <input 
                className="input" 
                type="text" 
                value={`${fees.merchant_fee_percentage}%`} 
                readOnly 
              />
            </div>
            <p className="help">Percentage-based merchant fee per transaction</p>
          </div>
        </Column>

        <Column isSize="1/2">
          <div className="field">
            <label className="label">Merchant Fee (Flat)</label>
            <div className="control">
              <input 
                className="input" 
                type="text" 
                value={`$${fees.merchant_fee_usd}`} 
                readOnly 
              />
            </div>
            <p className="help">Flat merchant fee per transaction</p>
          </div>
        </Column>

        {/* Customer Fees */}
        <Column isSize="1/2">
          <div className="field">
            <label className="label">Customer Fee (Percentage)</label>
            <div className="control">
              <input 
                className="input" 
                type="text" 
                value={`${fees.customer_fee_percentage}%`} 
                readOnly 
              />
            </div>
            <p className="help">Percentage-based customer fee per transaction</p>
          </div>
        </Column>

        <Column isSize="1/2">
          <div className="field">
            <label className="label">Customer Fee (Flat)</label>
            <div className="control">
              <input 
                className="input" 
                type="text" 
                value={`$${fees.customer_fee_usd}`} 
                readOnly 
              />
            </div>
            <p className="help">Flat customer fee per transaction</p>
          </div>
        </Column>

        {/* Platform Fee */}
        <Column isSize="1/2">
          <div className="field">
            <label className="label">Solvent Platform Fee</label>
            <div className="control">
              <input
                className="input"
                type="text"
                value={`${fees.solvent_platform_fee_percentage}%`}
                readOnly
              />
            </div>
            <p className="help">Platform fee charged by Solvent</p>
          </div>
        </Column>

        {/* Minimum Fee */}
        <Column isSize="1/2">
          <div className="field">
            <label className="label">Minimum Fee</label>
            <div className="control">
              <input 
                className="input" 
                type="text" 
                value={`$${fees.min_fee_usd}`} 
                readOnly 
              />
            </div>
            <p className="help">Minimum fee enforced per transaction</p>
          </div>
        </Column>
      </Columns>
    </UiFieldset>
  );
}

FeeBreakdownComponent.propTypes = {
  account: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  fees: propTypes.object,
  loading: propTypes.bool.isRequired,
  error: propTypes.string,
  fetchMerchantFees: propTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  fees: state.payment.fees.data,
  loading: state.payment.fees.loading,
  error: state.payment.fees.error,
});

const mapDispatchToProps = {
  fetchMerchantFees,
};

export const FeeBreakdown = connect(mapStateToProps, mapDispatchToProps)(FeeBreakdownComponent);
